import React, { useState, useEffect, Suspense } from 'react'
import HomePage from './components/home/HomePage'
import CollationStudioLandingPage from './components/home/CollationStudioLandingPage'
import BooksDashboard from './components/books/BooksDashboard'
import { WorkbenchErrorBoundary } from './components/workbench/WorkbenchErrorBoundary'
import { consumePendingSharedLedgerRecovery, SharedLedgerView } from './components/shared/SharedLedgerView'
import { useAppBooksStoreSubscription } from './store/booksStoreSubscriptions'
import { syncManager } from './lib/syncManager'
import { parseHashRoute } from './lib/hashRouteParser'
import { parseLedgerStudioPath } from './lib/ledgerStudioRoute'
import { parseReaderStudioPath } from './lib/readerStudioRoute'
import type { CharacterIndexProgressEvidenceKind } from './lib/characterIndexProgress'
import { parseSharedLedgerPath } from './lib/sharedLedgerRoute'
import { parseWorkbenchPath } from './lib/workbenchWorkflowBoundary'
import './App.css'
import './styles/compact.css'
import './styles/matrix.css'
import './styles/brand.css'
import { applyAnchorStorePatch, applyMergeUnitsAndCollapseByLabels } from './lib/anchorStorePatchApi'
import { getRuntimeFeatureFlag, RuntimeConfigProvider, useRuntimeConfig } from './config/runtimeConfig'
import { setLedgerSafePostgresPrimaryCutoverRequired } from './lib/ledgerSafePostgresPrimaryWriteBridge'

// Route views load lazily so the initial bundle stays small: the ledger
// studio, reader, and workbench are each multi-hundred-KB module graphs that
// only one route at a time needs.
const BookView = React.lazy(() => import('./components/books/BookView'))
const ChapterView = React.lazy(() => import('./components/books/ChapterView'))
const ChapterSegmentationFlow = React.lazy(() => import('./components/workflow/ChapterSegmentationFlow'))
const ChapterAlignment = React.lazy(() => import('./components/books/ChapterAlignment'))
const MultiWitnessEditor = React.lazy(() => import('./components/books/MultiWitnessEditor'))
const EnhancedMultiWitnessEditor = React.lazy(() => import('./components/books/EnhancedMultiWitnessEditor').then(module => ({ default: module.EnhancedMultiWitnessEditor })))
const WitnessSegmentationView = React.lazy(() => import('./components/books/WitnessSegmentationView'))
const WitnessResultsView = React.lazy(() => import('./components/books/WitnessResultsView'))
const CollationBrowser = React.lazy(() => import('./components/collations/CollationBrowser').then(module => ({ default: module.CollationBrowser })))
const AnchorSummaryView = React.lazy(() => import('./components/alignment/AnchorSummaryView'))
const WorkbenchView = React.lazy(() => import('./components/workbench/WorkbenchView').then(module => ({ default: module.WorkbenchView })))
const LedgerStudioView = React.lazy(() => import('./components/books/LedgerStudioView').then(module => ({ default: module.LedgerStudioView })))
const ReaderStudioView = React.lazy(() => import('./components/books/ReaderStudioView').then(module => ({ default: module.ReaderStudioView })))
const UserGuidePage = React.lazy(() => import('./components/docs/UserGuidePage').then(module => ({ default: module.UserGuidePage })))
const TextMappedAnalysisView = React.lazy(() => import('./components/books/TextMappedAnalysisView'))

const ENABLE_HOME_DASHBOARD =
  (process.env.REACT_APP_ENABLE_HOME_DASHBOARD || '').toLowerCase() === 'true'

const routeForDefaultRoute = (defaultRoute: string): string => {
  if (defaultRoute === 'landing') {
    return 'landing'
  }
  if (defaultRoute === 'workbench') {
    return 'workbench'
  }
  if (defaultRoute === 'books') {
    return 'books-dashboard'
  }
  return 'home'
}

const hashForDefaultRoute = (defaultRoute: string): string => {
  if (defaultRoute === 'landing') {
    return '#/landing'
  }
  if (defaultRoute === 'workbench') {
    return '#/workbench'
  }
  if (defaultRoute === 'books') {
    return '#/books'
  }
  return '#/'
}

const isCollationEnvironmentGuidePath = (pathname: string): boolean => {
  const segments = (pathname || '').split('/').filter(Boolean)
  return segments.length === 2
    && segments[0] === 'guide'
    && (segments[1] === 'collation-environment' || segments[1] === 'ledger-safe')
}

type PostgresPrimaryStartupState =
  | { status: 'inactive' | 'pending' | 'ready' }
  | { status: 'failed'; message: string }

const POSTGRES_PRIMARY_PUBLIC_ROUTES = new Set([
  'landing',
  'ledger-safe-guide',
  'shared-ledger',
])

const routeRequiresPostgresPrimaryStartup = (route: string): boolean => (
  route !== '' && !POSTGRES_PRIMARY_PUBLIC_ROUTES.has(route)
)

if (process.env.NODE_ENV === 'development') {
  ;(window as any).__anchorStorePatches = {
    applyAnchorStorePatch,
    applyMergeUnitsAndCollapseByLabels
  }
}

function AppContent() {
  const [currentRoute, setCurrentRoute] = useState('')
  const [routeParams, setRouteParams] = useState<Record<string, string>>({})
  const [postgresPrimaryStartup, setPostgresPrimaryStartup] =
    useState<PostgresPrimaryStartupState>({ status: 'inactive' })
  const [postgresPrimaryBootstrapAttempt, setPostgresPrimaryBootstrapAttempt] =
    useState(0)
  const { initializeDB, dbInitialized } = useAppBooksStoreSubscription()
  const { config, loaded: runtimeConfigLoaded } = useRuntimeConfig()
  const enableHomeDashboard = getRuntimeFeatureFlag(config, 'workbench.homeDashboard', ENABLE_HOME_DASHBOARD)
  const reducedWorkbenchEnabled = getRuntimeFeatureFlag(
    config,
    'workbench.reducedMode',
    process.env.NODE_ENV === 'production',
  )
  const legacyBooksRoutesEnabled = getRuntimeFeatureFlag(config, 'legacy.booksRoutes', process.env.NODE_ENV !== 'production')
  const ledgerSafeGuideEnabled = getRuntimeFeatureFlag(config, 'documentation.ledgerSafeGuide', false)
  const postgresFullMirrorEnabled =
    runtimeConfigLoaded &&
    config.profile === 'development' &&
    getRuntimeFeatureFlag(config, 'ledgerSafe.postgresTrial', false) &&
    getRuntimeFeatureFlag(config, 'ledgerSafe.postgresFullMirror', false)
  const postgresPrimaryEnabled =
    runtimeConfigLoaded &&
    getRuntimeFeatureFlag(config, 'ledgerSafe.postgresPrimary', false)
  const postgresPrimaryCutoverEnabled =
    postgresPrimaryEnabled &&
    getRuntimeFeatureFlag(config, 'ledgerSafe.postgresPrimaryCutover', false)
  const postgresPrimaryBootstrapEnabled =
    postgresPrimaryEnabled &&
    getRuntimeFeatureFlag(config, 'ledgerSafe.postgresPrimaryBootstrap', false)
  const postgresPrimaryFocusedCacheEnabled =
    postgresPrimaryBootstrapEnabled &&
    getRuntimeFeatureFlag(
      config,
      'ledgerSafe.postgresPrimaryFocusedCache',
      false,
    )
  const postgresPrimaryStartupRequired =
    routeRequiresPostgresPrimaryStartup(currentRoute)
  const postgresPrimaryStartupGateEnabled =
    postgresPrimaryStartupRequired
    && postgresPrimaryEnabled
    && (postgresPrimaryBootstrapEnabled || postgresPrimaryCutoverEnabled)

  // Initialize IndexedDB on app start
  useEffect(() => {
    if (!runtimeConfigLoaded) {
      return
    }
    if (
      postgresPrimaryStartupGateEnabled
      && postgresPrimaryStartup.status !== 'ready'
    ) {
      return
    }
    if (!dbInitialized) {
      initializeDB()
    }
  }, [
    dbInitialized,
    initializeDB,
    postgresPrimaryStartupGateEnabled,
    postgresPrimaryStartup.status,
    runtimeConfigLoaded,
  ])

  useEffect(() => {
    if (!postgresFullMirrorEnabled) {
      return
    }
    let disposed = false
    let uninstall: (() => void) | undefined
    void import('./lib/ledgerSafePostgresFullMirror').then(module => {
      if (disposed) {
        return
      }
      uninstall = module.installLedgerSafePostgresFullMirrorConsole()
    })
    return () => {
      disposed = true
      uninstall?.()
    }
  }, [postgresFullMirrorEnabled])

  useEffect(() => {
    setLedgerSafePostgresPrimaryCutoverRequired(false)
    if (!postgresPrimaryEnabled || !postgresPrimaryStartupRequired) {
      setPostgresPrimaryStartup({ status: 'inactive' })
      return () => {
        setLedgerSafePostgresPrimaryCutoverRequired(false)
      }
    }
    let disposed = false
    let uninstall: (() => void) | undefined
    setPostgresPrimaryStartup({
      status: postgresPrimaryBootstrapEnabled
        || postgresPrimaryCutoverEnabled
        ? 'pending'
        : 'ready',
    })
    void import('./lib/ledgerSafePostgresPrimary').then(async module => {
      if (disposed) return
      const installation = module.installLedgerSafePostgresPrimaryRuntime({
        mutationsEnabled: postgresPrimaryCutoverEnabled,
        focusedCacheEnabled: postgresPrimaryFocusedCacheEnabled,
      })
      uninstall = installation.uninstall
      if (
        postgresPrimaryCutoverEnabled
        && !postgresPrimaryBootstrapEnabled
      ) {
        throw new Error(
          'PostgreSQL cache bootstrap must be enabled before cutover.',
        )
      }
      if (postgresPrimaryBootstrapEnabled) {
        window.__ledgerSafePostgresPrimaryBootstrap = undefined
        const result = await installation.controller.bootstrapApplicationCache()
        if (disposed) return
        window.__ledgerSafePostgresPrimaryBootstrap = result
      }
      if (disposed) return
      setLedgerSafePostgresPrimaryCutoverRequired(
        postgresPrimaryCutoverEnabled,
      )
      setPostgresPrimaryStartup({ status: 'ready' })
    }).catch(error => {
      if (disposed) return
      const message = error instanceof Error
        ? error.message
        : 'PostgreSQL primary cache startup failed.'
      window.__ledgerSafePostgresPrimaryBootstrap = {
        status: 'failed',
        message,
      }
      setLedgerSafePostgresPrimaryCutoverRequired(false)
      setPostgresPrimaryStartup({ status: 'failed', message })
    })
    return () => {
      disposed = true
      uninstall?.()
      setLedgerSafePostgresPrimaryCutoverRequired(false)
    }
  }, [
    postgresPrimaryBootstrapEnabled,
    postgresPrimaryBootstrapAttempt,
    postgresPrimaryCutoverEnabled,
    postgresPrimaryEnabled,
    postgresPrimaryFocusedCacheEnabled,
    postgresPrimaryStartupRequired,
  ])

  // Initialize sync manager
  useEffect(() => {
    // Listen for witness updates to trigger sync
    const handleWitnessUpdate = (event: CustomEvent) => {
      const { witnessId } = event.detail
      syncManager.syncWitness(witnessId)
    }

    window.addEventListener('witness-updated', handleWitnessUpdate as EventListener)

    return () => {
      window.removeEventListener('witness-updated', handleWitnessUpdate as EventListener)
    }
  }, [])

  useEffect(() => {
    const updateRoute = () => {
      const pathnameSharedLedgerRoute = parseSharedLedgerPath(window.location.pathname)

      if (pathnameSharedLedgerRoute) {
        setCurrentRoute('shared-ledger')
        setRouteParams({ shareId: pathnameSharedLedgerRoute.shareId })
        return
      }

      if (isCollationEnvironmentGuidePath(window.location.pathname)) {
        setCurrentRoute(ledgerSafeGuideEnabled ? 'ledger-safe-guide' : routeForDefaultRoute(config.defaultRoute))
        setRouteParams({})
        return
      }

      const pathnameReaderStudioRoute = parseReaderStudioPath(
        window.location.pathname,
        window.location.search,
      )

      if (pathnameReaderStudioRoute) {
        const { target, returnTo } = pathnameReaderStudioRoute
        setCurrentRoute('chapter-reader')
        setRouteParams({
          bookId: target.bookId,
          chapterId: target.chapterId,
          witnessId: target.witnessId,
          evidenceKind: target.evidenceKind,
          rowId: target.rowId || '',
          unitLabel: target.unitLabel || '',
          characterId: target.characterId || '',
          evidenceId: target.evidenceId || '',
          returnTo: returnTo || '',
        })
        return
      }

      const pathnameWorkbenchRoute = parseWorkbenchPath(window.location.pathname)

      if (pathnameWorkbenchRoute) {
        if (pathnameWorkbenchRoute.view === 'ledger') {
          setCurrentRoute('chapter-ledger')
          setRouteParams({
            bookId: pathnameWorkbenchRoute.workId || pathnameWorkbenchRoute.projectId,
            chapterId: pathnameWorkbenchRoute.contextId,
            projectId: pathnameWorkbenchRoute.projectId,
            workId: pathnameWorkbenchRoute.workId,
            contextId: pathnameWorkbenchRoute.contextId
          })
          return
        }

        setCurrentRoute('workbench')
        setRouteParams({
          projectId: pathnameWorkbenchRoute.projectId,
          workId: pathnameWorkbenchRoute.workId,
          contextId: pathnameWorkbenchRoute.contextId
        })
        return
      }

      const pathnameLedgerStudioRoute = parseLedgerStudioPath(window.location.pathname)

      if (pathnameLedgerStudioRoute) {
        setCurrentRoute('chapter-ledger')
        setRouteParams({
          bookId: pathnameLedgerStudioRoute.bookId,
          chapterId: pathnameLedgerStudioRoute.chapterId
        })
        return
      }

      const parsed = parseHashRoute(window.location.hash)
      const { path, rest, query } = parsed
      if (getRuntimeFeatureFlag(config, 'auth.required', false) && (path === 'workbench' || path === 'books')) {
        const protectedPath = path === 'workbench' ? '/workbench' : '/books'
        if (process.env.NODE_ENV === 'test') {
          window.history.replaceState({}, '', protectedPath)
          window.dispatchEvent(new PopStateEvent('popstate'))
        } else {
          window.location.replace(protectedPath)
        }
        return
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('App.tsx - updateRoute called with location:', {
          pathname: window.location.pathname,
          hash: window.location.hash
        })
        console.log('App.tsx - parsed path:', path, 'rest:', rest)
      }

      if (!path || path === '') {
        setCurrentRoute(routeForDefaultRoute(config.defaultRoute))
        setRouteParams({})
      } else if (path === 'landing') {
        setCurrentRoute('landing')
        setRouteParams({})
      } else if (path === 'home') {
        setCurrentRoute('home')
        setRouteParams({})
      } else if (path === 'guide' && rest.length === 1 && (rest[0] === 'collation-environment' || rest[0] === 'ledger-safe')) {
        setCurrentRoute(ledgerSafeGuideEnabled ? 'ledger-safe-guide' : routeForDefaultRoute(config.defaultRoute))
        setRouteParams({})
      } else if (path === 'collations') {
        // Direct route to collations page
        setCurrentRoute('collations')
        setRouteParams({})
      } else if (path === 'workbench') {
        if (rest.length === 0) {
          setCurrentRoute('workbench')
          setRouteParams({})
        } else if (rest.length === 1) {
          setCurrentRoute('workbench')
          setRouteParams({ projectId: rest[0] })
        } else if (rest.length === 6 && rest[1] === 'works' && rest[3] === 'contexts' && rest[5] === 'ledger') {
          setCurrentRoute('chapter-ledger')
          setRouteParams({
            bookId: rest[2],
            chapterId: rest[4],
            projectId: rest[0],
            workId: rest[2],
            contextId: rest[4]
          })
        } else if (rest.length === 4 && rest[1] === 'contexts' && rest[3] === 'ledger') {
          setCurrentRoute('chapter-ledger')
          setRouteParams({
            bookId: rest[0],
            chapterId: rest[2],
            projectId: rest[0],
            contextId: rest[2]
          })
        } else if (rest.length === 5 && rest[1] === 'works' && rest[3] === 'contexts') {
          setCurrentRoute('workbench')
          setRouteParams({ projectId: rest[0], workId: rest[2], contextId: rest[4] })
        } else if (rest.length === 3 && rest[1] === 'contexts') {
          setCurrentRoute('workbench')
          setRouteParams({ projectId: rest[0], contextId: rest[2] })
        } else {
          setCurrentRoute('home')
          setRouteParams({})
        }
      } else if (path === 'books') {
        if (!legacyBooksRoutesEnabled) {
          window.history.replaceState({}, '', '/workbench')
          window.dispatchEvent(new PopStateEvent('popstate'))
          return
        }
        if (rest.length === 0) {
          setCurrentRoute('books-dashboard')
          setRouteParams({})
        } else if (rest.length === 1 && rest[0] === 'collations') {
          // Legacy route - redirect to new collations page
          setCurrentRoute('collations')
          setRouteParams({})
        } else if (rest.length === 1) {
          setCurrentRoute('book-view')
          setRouteParams({ bookId: rest[0] })
        } else if (rest.length === 3 && rest[1] === 'chapters') {
          setCurrentRoute('chapter-view')
          setRouteParams({ bookId: rest[0], chapterId: rest[2] })
        } else if (rest.length === 4 && rest[1] === 'chapters' && rest[3] === 'segment') {
          setCurrentRoute('chapter-segment')
          setRouteParams({ bookId: rest[0], chapterId: rest[2] })
        } else if (rest.length === 4 && rest[1] === 'chapters' && rest[3] === 'align') {
          setCurrentRoute('chapter-align')
          setRouteParams({ bookId: rest[0], chapterId: rest[2] })
        } else if (rest.length === 4 && rest[1] === 'chapters' && rest[3] === 'edit') {
          setCurrentRoute('multi-witness-edit')
          setRouteParams({ bookId: rest[0], chapterId: rest[2] })
        } else if (rest.length === 4 && rest[1] === 'chapters' && rest[3] === 'matrix-edit') {
          setCurrentRoute('matrix-witness-edit')
          setRouteParams({ bookId: rest[0], chapterId: rest[2] })
        } else if (rest.length === 4 && rest[1] === 'chapters' && rest[3] === 'analysis') {
          setCurrentRoute('chapter-analysis')
          setRouteParams({ bookId: rest[0], chapterId: rest[2] })
        } else if (rest.length === 4 && rest[1] === 'chapters' && rest[3] === 'anchors') {
          setCurrentRoute('chapter-anchors')
          setRouteParams({
            bookId: rest[0],
            chapterId: rest[2],
            clusterId: query.clusterId || '',
            anchorStoreId: query.anchorStoreId || ''
          })
        } else if (rest.length === 6 && rest[1] === 'chapters' && (rest[3] === 'witnesses' || rest[3] === 'witness')) {
          // Handle witness-related routes based on the last segment
          const action = rest[5] // 'segment', 'results', 'chunks', etc.
          if (action === 'results') {
            setCurrentRoute('witness-results')
            setRouteParams({ bookId: rest[0], chapterId: rest[2], witnessId: rest[4] })
          } else {
            // Default to segmentation view for 'segment', 'chunks', etc.
            setCurrentRoute('witness-segment')
            setRouteParams({ bookId: rest[0], chapterId: rest[2], witnessId: rest[4] })
          }
        }
      } else {
        setCurrentRoute(routeForDefaultRoute(config.defaultRoute))
        setRouteParams({})
      }
    }

    // Set initial route
    updateRoute()

    // Listen for route changes
    window.addEventListener('hashchange', updateRoute)
    window.addEventListener('popstate', updateRoute)

    return () => {
      window.removeEventListener('hashchange', updateRoute)
      window.removeEventListener('popstate', updateRoute)
    }
  }, [config, legacyBooksRoutesEnabled, ledgerSafeGuideEnabled])

  useEffect(() => {
    if (currentRoute !== 'workbench') {
      return
    }
    const searchParams = new URLSearchParams(window.location.search)
    if (searchParams.get('sharedLedgerRecovery') !== '1') {
      return
    }
    const pendingRecovery = consumePendingSharedLedgerRecovery()
    if (!pendingRecovery) {
      window.history.replaceState({}, '', '/workbench')
      return
    }
    window.history.replaceState(
      {},
      '',
      `/shared-ledgers/${encodeURIComponent(pendingRecovery.shareId)}?recover=${pendingRecovery.mode}`
    )
    window.dispatchEvent(new PopStateEvent('popstate'))
  }, [currentRoute])

  // Navigate root visits to the configured default route. Cloudflare Access
  // returns authenticated users to "/", so this must follow runtime config.
  useEffect(() => {
    if (
      runtimeConfigLoaded &&
      !window.location.hash
      && !parseSharedLedgerPath(window.location.pathname)
      && !parseWorkbenchPath(window.location.pathname)
      && !parseLedgerStudioPath(window.location.pathname)
      && !parseReaderStudioPath(window.location.pathname, window.location.search)
      && !isCollationEnvironmentGuidePath(window.location.pathname)
    ) {
      window.location.hash = hashForDefaultRoute(config.defaultRoute)
    }
  }, [config.defaultRoute, runtimeConfigLoaded])

  const renderCurrentView = () => {
    // Debug logging for route changes
    if (process.env.NODE_ENV === 'development') {
      console.log('App.tsx - renderCurrentView:', { currentRoute, routeParams })
    }

    switch (currentRoute) {
      case 'landing':
        return <CollationStudioLandingPage />
      case 'home':
        return enableHomeDashboard ? <HomePage /> : <BooksDashboard />
      case 'books-dashboard':
        return <BooksDashboard />
      case 'book-view':
        return <BookView bookId={routeParams.bookId} />
      case 'chapter-view':
        return <ChapterView bookId={routeParams.bookId} chapterId={routeParams.chapterId} />
      case 'chapter-segment':
        return <ChapterSegmentationFlow bookId={routeParams.bookId} chapterId={routeParams.chapterId} />
      case 'chapter-align':
        return <ChapterAlignment bookId={routeParams.bookId} chapterId={routeParams.chapterId} />
      case 'multi-witness-edit':
        return <MultiWitnessEditor bookId={routeParams.bookId} chapterId={routeParams.chapterId} />
      case 'matrix-witness-edit':
        return <EnhancedMultiWitnessEditor bookId={routeParams.bookId} chapterId={routeParams.chapterId} />
      case 'chapter-analysis':
        return <TextMappedAnalysisView bookId={routeParams.bookId} chapterId={routeParams.chapterId} />
      case 'chapter-ledger':
        return <LedgerStudioView bookId={routeParams.bookId} chapterId={routeParams.chapterId} />
      case 'chapter-reader':
        return (
          <ReaderStudioView
            target={{
              kind: 'reader-character-index',
              bookId: routeParams.bookId,
              chapterId: routeParams.chapterId,
              witnessId: routeParams.witnessId,
              evidenceKind: routeParams.evidenceKind as CharacterIndexProgressEvidenceKind,
              ...(routeParams.rowId ? { rowId: routeParams.rowId } : {}),
              ...(routeParams.unitLabel ? { unitLabel: routeParams.unitLabel } : {}),
              ...(routeParams.characterId ? { characterId: routeParams.characterId } : {}),
              ...(routeParams.evidenceId ? { evidenceId: routeParams.evidenceId } : {}),
            }}
            returnTo={routeParams.returnTo || undefined}
          />
        )
      case 'shared-ledger':
        return <SharedLedgerView shareId={routeParams.shareId} />
      case 'ledger-safe-guide':
        return <UserGuidePage />
      case 'chapter-anchors':
        return (
          <AnchorSummaryView
            chapterId={routeParams.chapterId}
            clusterId={routeParams.clusterId}
            anchorStoreId={routeParams.anchorStoreId}
            onClose={() => { window.location.hash = `#/books/${routeParams.bookId}/chapters/${routeParams.chapterId}` }}
          />
        )
      case 'witness-segment':
        console.log('Rendering WitnessSegmentationView with params:', routeParams)
        return <WitnessSegmentationView bookId={routeParams.bookId} chapterId={routeParams.chapterId} witnessId={routeParams.witnessId} />
      case 'witness-results':
        console.log('Rendering WitnessResultsView with params:', routeParams)
        return <WitnessResultsView bookId={routeParams.bookId} chapterId={routeParams.chapterId} witnessId={routeParams.witnessId} />
      case 'collations':
        console.log('Rendering Collations page')
        return (
          <div className="container mx-auto px-4 py-8">
            <div className="mb-6">
              <h1 className="text-3xl font-bold mb-2">Alignment Collations</h1>
              <p className="text-muted-foreground">
                Manage saved alignment collations that serve as anchors for further witness alignment.
              </p>
            </div>
            <CollationBrowser />
          </div>
        )
      case 'workbench':
        return (
          <WorkbenchErrorBoundary>
            <WorkbenchView
              projectId={routeParams.projectId}
              workId={routeParams.workId}
              contextId={routeParams.contextId}
              reducedMode={reducedWorkbenchEnabled}
            />
          </WorkbenchErrorBoundary>
        )
      default:
        console.log('Falling back to home view for route:', currentRoute)
        return enableHomeDashboard ? <HomePage /> : <BooksDashboard />
    }
  }

  if (!runtimeConfigLoaded) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50 text-sm text-slate-600">
        Loading application configuration…
      </div>
    )
  }

  if (
    postgresPrimaryStartupGateEnabled
    && postgresPrimaryStartup.status !== 'ready'
    && postgresPrimaryStartup.status !== 'failed'
  ) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50 px-6">
        <div className="max-w-md rounded-xl border border-slate-200 bg-white p-6 text-left shadow-sm">
          <h1 className="text-lg font-semibold text-slate-900">
            Preparing PostgreSQL data
          </h1>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            {postgresPrimaryFocusedCacheEnabled
              ? 'Variorum is refreshing its local working data from PostgreSQL. Existing browser copies are replaced automatically; chapters and history load only when you open them.'
              : 'Ledger Safe is copying or synchronizing the authoritative PostgreSQL data before opening the workbench. A first-time browser profile can take several minutes.'}
          </p>
        </div>
      </div>
    )
  }

  if (
    postgresPrimaryStartupGateEnabled
    && postgresPrimaryStartup.status === 'failed'
  ) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50 px-6">
        <div
          className="max-w-lg rounded-xl border border-red-200 bg-white p-6 text-left shadow-sm"
          role="alert"
        >
          <h1 className="text-lg font-semibold text-slate-900">
            PostgreSQL cache setup stopped safely
          </h1>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            {postgresPrimaryStartup.message}
          </p>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            The workbench remains closed and no PostgreSQL-first edits are
            enabled.
          </p>
          <button
            type="button"
            className="mt-4 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
            onClick={() => setPostgresPrimaryBootstrapAttempt(attempt => attempt + 1)}
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="App">
      <Suspense fallback={<div className="flex h-screen items-center justify-center text-sm text-gray-500">Loading…</div>}>
        {renderCurrentView()}
      </Suspense>
    </div>
  )
}

function App() {
  return (
    <RuntimeConfigProvider>
      <AppContent />
    </RuntimeConfigProvider>
  )
}

export default App
